import { TERMINAL_KIND_LABEL, TERMINAL_KINDS, isValidTerminalCode } from '@didto/domain';
import type { TerminalKind } from '@didto/domain';
import {
  Badge,
  Button,
  Checkbox,
  ConfirmSheet,
  EmptyState,
  ErrorState,
  Field,
  Input,
  Loading,
  PageHeader,
  SectionHead,
  Select,
  Sheet,
  StatusChip,
} from '@didto/ui';
import { useQuery } from '@tanstack/react-query';
import type { FormEvent } from 'react';
import { useId, useState } from 'react';
import { useSession } from '@/app/session';
import { useClock } from '@/app/useClock';
import { useSetting } from '@/app/useSetting';
import { useCommand } from '@/app/useCommand';
import type { Row } from '@/lib/db';
import { DEVICE_STATE, deviceState } from '@/lib/deviceState';
import { errorText } from '@/lib/errors';
import { useOnline } from '@/lib/network';
import { fetchDrawers, shiftKeys } from '@/lib/shifts';
import type { CashDrawer } from '@/lib/shifts';
import { supabase } from '@/lib/supabase';
import './devices.css';

type Terminal = Pick<
  Row<'terminal'>,
  | 'id'
  | 'name'
  | 'code'
  | 'kind'
  | 'status'
  | 'production_station_id'
  | 'last_seen_at'
  | 'last_app_version'
  | 'enrolled_at'
  | 'revoked_at'
  | 'revoke_reason'
  | 'archived_at'
  | 'cash_drawer_id'
>;
type Station = Pick<Row<'production_station'>, 'id' | 'name' | 'code'>;
export function DevicesPage() {
  const s = useSession();
  const locationId = s.me?.active_location_id ?? '';
  const canManage = s.can('device.manage');
  // ADR-037: every change here is a command; none starts while the server cannot be reached
  const online = useOnline();
  // ADR-024: drawers and linking tablets to them are the owner's (shift.adjust), from their
  // signed-in account
  const canDrawers = s.me?.principal === 'human' && s.can('shift.adjust');
  const cmd = useCommand([
    ['terminals'],
    ['terminal-stations'],
    ['terminals-dashboard'],
    ['sessions'],
    [...shiftKeys.drawers(locationId)],
  ]);
  const [editing, setEditing] = useState<Terminal | 'new' | null>(null);
  const [code, setCode] = useState<{ terminal: Terminal; code: string; expires_at: string } | null>(
    null,
  );

  const terminals = useQuery({
    queryKey: ['terminals', locationId],
    enabled: !!locationId,
    refetchInterval: 30_000,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('terminal')
        .select(
          'id, name, code, kind, status, production_station_id, last_seen_at, last_app_version, enrolled_at, revoked_at, revoke_reason, archived_at, cash_drawer_id',
        )
        .eq('location_id', locationId)
        .is('archived_at', null)
        .order('code');
      if (error) throw error;
      return data;
    },
  });
  // ADR-045: a Kitchen / Bar display's stations. On a server without them (older than ADR-045) the table is missing,
  // and the page then shows and edits the one station as before; any other failed read is only retried.
  const assignments = useQuery({
    queryKey: ['terminal-stations', locationId],
    enabled: !!locationId,
    retry: (n, e) => !isMissingTable(e) && n < 1,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('terminal_production_station')
        .select('terminal_id, production_station_id')
        .eq('location_id', locationId);
      if (error) throw error;
      return data;
    },
  });
  const legacyStations =
    assignments.isError && assignments.data === undefined && isMissingTable(assignments.error);
  const drawers = useQuery({
    queryKey: shiftKeys.drawers(locationId),
    enabled: !!locationId,
    queryFn: () => fetchDrawers(locationId),
  });
  const activeDrawers = (drawers.data ?? []).filter((d) => !d.archived_at);
  const stations = useQuery({
    queryKey: ['stations', locationId],
    enabled: !!locationId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('production_station')
        .select('id, name, code')
        .eq('location_id', locationId)
        .is('archived_at', null)
        .order('sort');
      if (error) throw error;
      return data;
    },
  });
  // a display's sheet opens only once its stations are known (the stations and the display's set): a sheet started
  // from the home alone, or from no station, would save a Kitchen + Bar display as Kitchen-only
  const stationsKnown =
    stations.data !== undefined && (assignments.data !== undefined || legacyStations);
  const sessions = useQuery({
    queryKey: ['sessions', locationId],
    enabled: !!locationId,
    refetchInterval: 30_000,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('terminal_session')
        .select('id, terminal_id, employee_id, opened_at, last_activity_at')
        .eq('location_id', locationId)
        .is('ended_at', null);
      if (error) throw error;
      return data;
    },
  });
  const employees = useQuery({
    queryKey: ['employees'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('employee')
        .select('id, display_name')
        .order('display_name');
      if (error) throw error;
      return data;
    },
  });

  const now = useClock(15_000);
  const ONLINE_SECONDS = Number(useSetting('terminal.online_threshold_seconds'));
  /** Online means the device checked in recently (not this browser's own connection). */
  const stateOf = (t: Terminal) => deviceState(t, now, ONLINE_SECONDS);
  const stationName = (id: string | null) => stations.data?.find((x) => x.id === id)?.name ?? null;
  /** A display's stations in the stations' own order (its home alone on an older server). */
  const stationIdsOf = (t: Terminal): string[] => {
    if (t.kind !== 'kds') return [];
    if (legacyStations || !assignments.data)
      return t.production_station_id ? [t.production_station_id] : [];
    // (while the read is pending this is only a label: the sheet waits for it, see stationsKnown)
    const mine = new Set(
      assignments.data.filter((a) => a.terminal_id === t.id).map((a) => a.production_station_id),
    );
    return (stations.data ?? []).filter((st) => mine.has(st.id)).map((st) => st.id);
  };
  const stationsLabel = (t: Terminal): string | null => {
    const ids = stationIdsOf(t);
    const names = ids.map((id) => stationName(id)).filter((n): n is string => !!n);
    if (names.length === 0) return null;
    const home = stationName(t.production_station_id);
    return names.length > 1 && home ? `${names.join(' + ')}, opens on ${home}` : names.join(' + ');
  };
  const sessionFor = (t: Terminal) => sessions.data?.find((x) => x.terminal_id === t.id);
  const empName = (id: string) => employees.data?.find((e) => e.id === id)?.display_name ?? '…';
  const drawerName = (id: string | null) =>
    id ? ((drawers.data ?? []).find((d) => d.id === id)?.name ?? 'cash drawer') : null;

  const newCode = async (t: Terminal) => {
    const r = await cmd.mutateAsync({
      kind: 'terminal.create_enrollment_code',
      payload: { terminal_id: t.id },
    });
    setCode({ terminal: t, code: String(r.code), expires_at: String(r.expires_at) });
  };

  return (
    <div className="d-page">
      <PageHeader
        eyebrow="Hardware"
        title="Devices"
        subtitle="The café's tablets and screens: enrol a new one, rename it, link a POS tablet to its cash drawer, or revoke one that is lost."
        actions={
          canManage ? (
            <Button variant="primary" onClick={() => setEditing('new')} data-testid="new-terminal">
              New terminal
            </Button>
          ) : undefined
        }
      />
      <div className="d-list" data-testid="terminal-list">
        {terminals.isLoading && <Loading rows={3} label="Loading terminals…" />}
        {terminals.isError && (
          <ErrorState
            title="Could not load the terminals"
            message={errorText(terminals.error)}
            onRetry={() => void terminals.refetch()}
          />
        )}
        {(terminals.data ?? []).map((t) => {
          const sess = sessionFor(t);
          const state = stateOf(t);
          const look = DEVICE_STATE[state];
          return (
            <div
              key={t.id}
              className="d-row devices__row"
              data-state={state}
              data-testid={`terminal-row-${t.id}`}
            >
              <div className="d-row__main">
                {/* the title keeps its code as a direct child (e2e terminalRow finds a row by it) */}
                <div className="devices__head">
                  <div className="d-row__title">
                    <span className="d-mono">{t.code}</span> · {t.name}
                  </div>
                  <StatusChip tone={look.tone} shape={look.shape}>
                    {look.label}
                  </StatusChip>
                </div>
                <div className="d-row__meta d-inline">
                  <span data-testid={`terminal-stations-of-${t.code}`}>
                    {TERMINAL_KIND_LABEL[t.kind]}
                    {stationsLabel(t) ? ` · ${stationsLabel(t)}` : ''}
                  </span>
                  {state === 'offline' && t.last_seen_at && (
                    <span>last seen {new Date(t.last_seen_at).toLocaleTimeString()}</span>
                  )}
                  {state === 'revoked' && t.revoke_reason && <span>{t.revoke_reason}</span>}
                  {t.last_app_version && (
                    <span className="d-mono d-faint">v{t.last_app_version}</span>
                  )}
                  {sess && <Badge tone="info">{empName(sess.employee_id)} signed in</Badge>}
                  {t.kind === 'pos' && !(canManage && canDrawers) && (
                    <Badge tone={t.cash_drawer_id ? 'neutral' : 'warning'}>
                      {drawerName(t.cash_drawer_id) ?? 'no cash drawer'}
                    </Badge>
                  )}
                </div>
              </div>
              {canManage && (
                <div className="d-actions">
                  {t.kind === 'pos' && canDrawers && (
                    <Select
                      value={t.cash_drawer_id ?? ''}
                      disabled={!online}
                      onChange={(e) =>
                        void cmd.mutateAsync({
                          kind: 'terminal.bind_drawer',
                          payload: { terminal_id: t.id, drawer_id: e.target.value || null },
                        })
                      }
                      aria-label={`Cash drawer of ${t.name}`}
                      className="devices__drawer"
                      data-testid={`terminal-drawer-${t.code}`}
                    >
                      <option value="">No cash drawer</option>
                      {activeDrawers.map((d) => (
                        <option key={d.id} value={d.id}>
                          {d.name}
                        </option>
                      ))}
                    </Select>
                  )}
                  {t.status !== 'active' && (
                    <Button
                      size="sm"
                      variant="primary"
                      disabled={!online}
                      onClick={() => void newCode(t)}
                      data-testid={`enroll-code-${t.id}`}
                    >
                      Enrolment code
                    </Button>
                  )}
                  {t.status === 'active' && (
                    <Button
                      size="sm"
                      variant="ghost"
                      disabled={!online}
                      onClick={() => void newCode(t)}
                      data-testid={`reenroll-code-${t.id}`}
                    >
                      Re-enrol
                    </Button>
                  )}
                  <Button
                    size="sm"
                    variant="ghost"
                    disabled={t.kind === 'kds' && !stationsKnown}
                    onClick={() => setEditing(t)}
                    data-testid={`edit-terminal-${t.id}`}
                  >
                    Edit
                  </Button>
                </div>
              )}
            </div>
          );
        })}
        {terminals.isSuccess && terminals.data.length === 0 && (
          <EmptyState title="No terminals yet">
            Create one per tablet: Counter POS, Kitchen KDS, Bar KDS…
          </EmptyState>
        )}
      </div>

      {canDrawers && (
        <Drawers
          drawers={activeDrawers}
          terminals={terminals.data ?? []}
          onCreate={(name) =>
            cmd.mutateAsync({
              kind: 'cash_drawer.create',
              payload: { location_id: locationId, name },
            })
          }
          onRename={(d, name) =>
            cmd.mutateAsync({ kind: 'cash_drawer.update', payload: { drawer_id: d.id, name } })
          }
          onArchive={(d) =>
            cmd.mutateAsync({ kind: 'cash_drawer.archive', payload: { drawer_id: d.id } })
          }
        />
      )}

      {editing && (
        <TerminalSheet
          terminal={editing === 'new' ? null : editing}
          stations={stations.data ?? []}
          stationIds={editing === 'new' ? [] : stationIdsOf(editing)}
          legacyStations={legacyStations}
          existingCodes={(terminals.data ?? [])
            .filter((t) => editing === 'new' || t.id !== editing.id)
            .map((t) => t.code)}
          onClose={() => setEditing(null)}
          onSave={async (v) => {
            if (editing === 'new')
              await cmd.mutateAsync({
                kind: 'terminal.create',
                payload: { location_id: locationId, ...v },
              });
            else
              await cmd.mutateAsync({
                kind: 'terminal.update',
                payload: { terminal_id: editing.id, ...v },
              });
            setEditing(null);
          }}
          onRevoke={
            editing !== 'new' && editing.status !== 'revoked'
              ? async (reason) => {
                  await cmd.mutateAsync({
                    kind: 'terminal.revoke',
                    payload: { terminal_id: editing.id, reason },
                  });
                  setEditing(null);
                }
              : undefined
          }
          onArchive={
            editing !== 'new' && editing.status !== 'active'
              ? async () => {
                  await cmd.mutateAsync({
                    kind: 'terminal.archive',
                    payload: { terminal_id: editing.id },
                  });
                  setEditing(null);
                }
              : undefined
          }
        />
      )}
      {code && (
        <Sheet open onClose={() => setCode(null)} title={`Enrol ${code.terminal.name}`}>
          <div className="devices__enrol">
            <p className="d-muted">
              On the tablet, open <span className="d-mono">/enroll</span> and enter this code. It
              works once and expires at {new Date(code.expires_at).toLocaleTimeString()}.
            </p>
            <div className="devices__code" data-testid="enrollment-code">
              {code.code}
            </div>
            <Button variant="primary" size="lg" block onClick={() => setCode(null)}>
              Done
            </Button>
          </div>
        </Sheet>
      )}
    </div>
  );
}

/** PostgREST's answer for a table it does not know: a server older than ADR-045. */
function isMissingTable(e: unknown): boolean {
  const code = (e as { code?: string } | null)?.code;
  return code === 'PGRST205' || code === '42P01';
}

function TerminalSheet(props: {
  terminal: Terminal | null;
  stations: Station[];
  /** The display's assigned stations (ADR-045). */
  stationIds: string[];
  /** The server is older than ADR-045: one station, sent the old way. */
  legacyStations: boolean;
  existingCodes: string[];
  onClose: () => void;
  onSave: (v: {
    name: string;
    code: string;
    kind: TerminalKind;
    production_station_ids?: string[];
    production_station_id: string | null;
  }) => Promise<void>;
  onRevoke?: (reason: string) => Promise<void>;
  onArchive?: () => Promise<void>;
}) {
  const t = props.terminal;
  const online = useOnline();
  const [name, setName] = useState(t?.name ?? '');
  const [code, setCode] = useState(t?.code ?? '');
  const [kind, setKind] = useState<TerminalKind>(t?.kind ?? 'pos');
  const [station, setStation] = useState(t?.production_station_id ?? '');
  // the mode the sheet opened in stays while it is open (a later read must not switch it under the owner)
  const [legacy] = useState(props.legacyStations);
  // ADR-045: the display's stations (one tab each) and its home, the tab it opens on: always one of them
  const [picked, setPicked] = useState<string[]>(props.stationIds);
  const [home, setHome] = useState(
    t?.production_station_id && props.stationIds.includes(t.production_station_id)
      ? t.production_station_id
      : (props.stationIds[0] ?? ''),
  );
  const [stationsTouched, setStationsTouched] = useState(false);
  const stationsGroup = useId();
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [confirmArchive, setConfirmArchive] = useState(false);
  const [archiving, setArchiving] = useState(false);
  // in the stations' own order
  const pickedIds = props.stations.filter((st) => picked.includes(st.id)).map((st) => st.id);
  const toggleStation = (id: string, on: boolean) => {
    setStationsTouched(true);
    const next = on ? [...picked, id] : picked.filter((x) => x !== id);
    setPicked(next);
    // one station is the one the display opens on; the first ticked is the home until the owner picks another; the
    // home unticked with several left: the owner chooses
    if (next.length === 1) setHome(next[0]!);
    else if (on && !home) setHome(id);
    else if (!on && home === id) setHome('');
  };
  const stationsError =
    kind !== 'kds' || legacy
      ? null
      : pickedIds.length === 0
        ? 'Choose at least one station'
        : !pickedIds.includes(home)
          ? 'Choose the station this display opens on'
          : null;
  const codeError =
    code && !isValidTerminalCode(code.toUpperCase())
      ? '1–3 letters or digits'
      : props.existingCodes.includes(code.toUpperCase())
        ? 'Already used at this location'
        : null;
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (codeError) return;
    if (stationsError) {
      setStationsTouched(true);
      return;
    }
    setBusy(true);
    try {
      await props.onSave(
        legacy
          ? {
              name: name.trim(),
              code: code.toUpperCase(),
              kind,
              production_station_id: kind === 'kds' && station ? station : null,
            }
          : {
              name: name.trim(),
              code: code.toUpperCase(),
              kind,
              production_station_ids: kind === 'kds' ? pickedIds : [],
              production_station_id: kind === 'kds' ? home : null,
            },
      );
    } finally {
      setBusy(false);
    }
  };
  return (
    <>
      <Sheet open onClose={props.onClose} title={t ? `Edit ${t.name}` : 'New terminal'}>
        <form onSubmit={(e) => void submit(e)} className="d-stack">
          <Field label="Name" help="e.g. Counter, Kitchen screen, Bar screen">
            {(id) => (
              <Input
                id={id}
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                maxLength={60}
                autoFocus
                data-testid="terminal-name"
              />
            )}
          </Field>
          <Field label="Code" help="Part of future order numbers: 0926-A-1048" error={codeError}>
            {(id) => (
              <Input
                id={id}
                value={code}
                onChange={(e) => setCode(e.target.value.toUpperCase())}
                required
                maxLength={3}
                aria-invalid={codeError ? true : undefined}
                className="d-mono"
                data-testid="terminal-code"
              />
            )}
          </Field>
          <Field label="Kind">
            {(id) => (
              <Select
                id={id}
                value={kind}
                onChange={(e) => setKind(e.target.value as TerminalKind)}
                data-testid="terminal-kind"
              >
                {TERMINAL_KINDS.map((k) => (
                  <option key={k} value={k}>
                    {TERMINAL_KIND_LABEL[k]}
                  </option>
                ))}
              </Select>
            )}
          </Field>
          {kind === 'kds' && !legacy && (
            <div className="d-field" role="group" aria-labelledby={stationsGroup}>
              <span className="d-label" id={stationsGroup}>
                Production stations
              </span>
              <div className="d-stack d-stack--tight" data-testid="terminal-stations">
                {props.stations.map((st) => (
                  <Checkbox
                    key={st.id}
                    checked={picked.includes(st.id)}
                    onChange={(on) => toggleStation(st.id, on)}
                    label={st.name}
                    testId={`terminal-station-${st.code}`}
                  />
                ))}
              </div>
              {stationsTouched && stationsError ? (
                <div className="d-error" role="alert">
                  {stationsError}
                </div>
              ) : (
                <div className="d-help">
                  Which tabs this display shows, one per station. Tick Kitchen and Bar for one
                  tablet that runs both.
                </div>
              )}
            </div>
          )}
          {kind === 'kds' && !legacy && pickedIds.length > 1 && (
            <Field label="Opens on" help="The tab this display shows after a staff member signs in">
              {(id) => (
                <Select
                  id={id}
                  value={pickedIds.includes(home) ? home : ''}
                  onChange={(e) => setHome(e.target.value)}
                  data-testid="terminal-home-station"
                >
                  <option value="">Choose…</option>
                  {props.stations
                    .filter((st) => pickedIds.includes(st.id))
                    .map((st) => (
                      <option key={st.id} value={st.id}>
                        {st.name}
                      </option>
                    ))}
                </Select>
              )}
            </Field>
          )}
          {kind === 'kds' && legacy && (
            <Field label="Production station" help="Which screen this display shows">
              {(id) => (
                <Select
                  id={id}
                  value={station}
                  onChange={(e) => setStation(e.target.value)}
                  data-testid="terminal-station"
                >
                  <option value="">Choose…</option>
                  {props.stations.map((st) => (
                    <option key={st.id} value={st.id}>
                      {st.name}
                    </option>
                  ))}
                </Select>
              )}
            </Field>
          )}
          <div className="d-sheet__actions">
            <Button
              type="submit"
              variant="primary"
              busy={busy}
              disabled={!online}
              data-testid="save-terminal"
            >
              Save
            </Button>
          </div>
        </form>
        {t && (
          <section className="d-stack">
            <hr className="d-divider" />
            <h3 className="d-eyebrow">Danger zone</h3>
            {props.onRevoke && (
              <>
                <Field
                  label="Reason for revoking"
                  help="Revoking signs the tablet out immediately and blocks it until it is enrolled again with a new code."
                >
                  {(id) => (
                    <Input
                      id={id}
                      placeholder="Reason (e.g. lost tablet, replaced)"
                      value={reason}
                      onChange={(e) => setReason(e.target.value)}
                      data-testid="revoke-reason"
                    />
                  )}
                </Field>
                <div className="d-actions">
                  <Button
                    variant="danger"
                    disabled={!online || !reason.trim()}
                    onClick={() => void props.onRevoke!(reason.trim())}
                    data-testid="revoke-terminal"
                  >
                    Revoke device
                  </Button>
                </div>
              </>
            )}
            {props.onArchive && (
              <div className="d-actions">
                <Button
                  variant="ghost"
                  disabled={!online}
                  onClick={() => setConfirmArchive(true)}
                  data-testid="archive-terminal"
                >
                  Archive terminal
                </Button>
              </div>
            )}
          </section>
        )}
      </Sheet>
      {t && props.onArchive && (
        <ConfirmSheet
          open={confirmArchive}
          title={`Archive ${t.name}?`}
          confirmLabel="Archive terminal"
          busy={archiving}
          onClose={() => setConfirmArchive(false)}
          onConfirm={() => {
            setArchiving(true);
            // on success the terminal sheet closes with this one; a refusal is toasted
            void props.onArchive!().catch(() => {
              setArchiving(false);
              setConfirmArchive(false);
            });
          }}
        >
          <p>The terminal leaves the device list. Its history stays.</p>
          <p className="d-muted">An archived terminal cannot be brought back from here.</p>
        </ConfirmSheet>
      )}
    </>
  );
}

/**
 * Cash drawers (ADR-024). Each location starts with its Main Drawer; the counter POS tablet is
 * linked to it above. More drawers are optional (the owner adds one only for a second till).
 */
function Drawers({
  drawers,
  terminals,
  onCreate,
  onRename,
  onArchive,
}: {
  drawers: CashDrawer[];
  terminals: Terminal[];
  onCreate: (name: string) => Promise<unknown>;
  onRename: (d: CashDrawer, name: string) => Promise<unknown>;
  onArchive: (d: CashDrawer) => Promise<unknown>;
}) {
  const online = useOnline();
  const [editing, setEditing] = useState<CashDrawer | 'new' | null>(null);
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const linked = (d: CashDrawer) => terminals.filter((t) => t.cash_drawer_id === d.id);
  const save = async (e: FormEvent) => {
    e.preventDefault();
    if (!editing || !name.trim()) return;
    setBusy(true);
    try {
      if (editing === 'new') await onCreate(name.trim());
      else await onRename(editing, name.trim());
      setEditing(null);
    } finally {
      setBusy(false);
    }
  };
  return (
    <section className="d-stack d-stack--tight" data-testid="drawers">
      <SectionHead title="Cash drawers">
        <Button
          size="sm"
          variant="ghost"
          onClick={() => {
            setEditing('new');
            setName('');
          }}
          data-testid="drawer-new"
        >
          Add drawer
        </Button>
      </SectionHead>
      <p className="d-help">
        Link the counter POS tablet to its drawer before the first shift. Tablets without a drawer
        take no payments.
      </p>
      <div className="d-list">
        {drawers.map((d) => {
          const tablets = linked(d);
          return (
            <div key={d.id} className="d-row" data-testid={`drawer-row-${d.name}`}>
              <div className="d-row__main">
                <div className="d-row__title">
                  {d.name} {d.is_main && <Badge>Main</Badge>}
                </div>
                <div className="d-row__meta">
                  {tablets.length > 0
                    ? `Linked: ${tablets.map((t) => `${t.code} · ${t.name}`).join(', ')}`
                    : 'No tablet linked'}
                </div>
              </div>
              <div className="d-actions">
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => {
                    setEditing(d);
                    setName(d.name);
                  }}
                >
                  Rename
                </Button>
                {!d.is_main && (
                  <Button
                    size="sm"
                    variant="quiet"
                    disabled={!online}
                    onClick={() => void onArchive(d)}
                    data-testid={`drawer-archive-${d.name}`}
                  >
                    Archive
                  </Button>
                )}
              </div>
            </div>
          );
        })}
      </div>
      <Sheet
        open={!!editing}
        onClose={() => setEditing(null)}
        title={editing === 'new' ? 'Add drawer' : 'Rename drawer'}
      >
        <form className="d-stack" onSubmit={(e) => void save(e)}>
          <Field label="Name" help="e.g. Bar Drawer">
            {(id) => (
              <Input
                id={id}
                value={name}
                onChange={(e) => setName(e.target.value)}
                maxLength={40}
                required
                autoFocus
                data-testid="drawer-name"
              />
            )}
          </Field>
          <div className="d-sheet__actions">
            <Button
              type="submit"
              variant="primary"
              busy={busy}
              disabled={!online}
              data-testid="drawer-save"
            >
              Save
            </Button>
          </div>
        </form>
      </Sheet>
    </section>
  );
}
